import type { Audio, Persisted, Video } from '@tunasong/schemas'
import type { HTMLAttributes } from 'react'
import { useCallback, useEffect, useState } from 'react'

export interface PlayListProps extends HTMLAttributes<HTMLDivElement> {
  className?: string
  name?: string
  entities: Persisted<Audio | Video>[]
  /** Capture keyboard events and control the playlist */
  keyboardControl?: boolean
  /** The sourceEntityId to use as a backlink */
  sourceEntityId?: string
  ref?: React.Ref<HTMLDivElement>
}

export const useScrollIndicator = (props: { containerRef: React.RefObject<HTMLElement | null> }) => {
  const { containerRef } = props

  const [hasItemsBefore, setHasItemsBefore] = useState(false)
  const [hasItemsAfter, setHasItemsAfter] = useState(false)

  const checkVisibility = useCallback(() => {
    const menuList = containerRef.current
    if (!menuList) {
      setHasItemsBefore(false)
      setHasItemsAfter(false)
      return
    }

    const firstItem = menuList.children[0] as HTMLElement
    const lastItem = menuList.children[menuList.children.length - 1] as HTMLElement

    if (!firstItem || !lastItem) {
      return
    }

    const containerRect = menuList.getBoundingClientRect()
    const firstItemRect = firstItem.getBoundingClientRect()
    const lastItemRect = lastItem.getBoundingClientRect()

    // Check if first item is partially or fully scrolled out of view (top)
    setHasItemsBefore(firstItemRect.top < containerRect.top)

    // Check if last item is partially or fully scrolled out of view (bottom)
    setHasItemsAfter(lastItemRect.bottom > containerRect.bottom)
  }, [containerRef])

  useEffect(() => {
    checkVisibility()
  }, [checkVisibility])

  return {
    hasItemsBefore,
    hasItemsAfter,
    checkVisibility,
  }
}
